import * as React from 'react';
import { useState, useEffect } from 'react';
import PlatformService, {
  PlatformTenant,
  CreateTenantRequest,
  UpdateTenantRequest,
} from '../services/PlatformService';

const BILLING_STATE_OPTIONS = ['trialing', 'active', 'billing_required', 'past_due'];

function PlatformAdminPage(): React.JSX.Element {
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTenantRequest>({
    tenant_name: '',
    admin_email: '',
    password: '',
    trial_days: 60,
    is_design_partner: false,
    special_code: '',
  });
  const [creating, setCreating] = useState(false);
  const [editingTenantId, setEditingTenantId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<UpdateTenantRequest>({});

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await PlatformService.getTenants();
      setTenants(data);
      setError('');
    } catch (err) {
      setError('Failed to load tenants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await PlatformService.createTenant(createForm);
      setCreateForm({
        tenant_name: '',
        admin_email: '',
        password: '',
        trial_days: 60,
        is_design_partner: false,
        special_code: '',
      });
      setShowCreateForm(false);
      await loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const startEditing = (tenant: PlatformTenant) => {
    setEditingTenantId(tenant.id);
    setEditForm({
      billing_state: tenant.billing_state,
      trial_ends_at: tenant.trial_ends_at ? tenant.trial_ends_at.split('T')[0] : '',
      is_design_partner: tenant.is_design_partner,
      special_code: tenant.special_code,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTenantId) return;
    try {
      const payload: UpdateTenantRequest = { ...editForm };
      if (payload.trial_ends_at) {
        payload.trial_ends_at = new Date(payload.trial_ends_at).toISOString();
      }
      await PlatformService.updateTenant(editingTenantId, payload);
      setEditingTenantId(null);
      await loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update tenant');
    }
  };

  const cancelEdit = () => {
    setEditingTenantId(null);
    setEditForm({});
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const billingStateBadge = (state: string) => {
    const colors: Record<string, { bg: string; color: string }> = {
      trialing: { bg: '#dbeafe', color: '#1e40af' },
      active: { bg: '#dcfce7', color: '#166534' },
      billing_required: { bg: '#fef3c7', color: '#92400e' },
      past_due: { bg: '#fecaca', color: '#991b1b' },
    };
    const style = colors[state] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: style.bg,
          color: style.color,
        }}
      >
        {state}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Loading tenants...
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
          Platform Admin
        </h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '8px 16px',
            background: '#0E1333',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {showCreateForm ? 'Cancel' : '+ New Tenant'}
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: '#fef2f2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}
        >
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#991b1b',
              fontWeight: 600,
            }}
          >
            ×
          </button>
        </div>
      )}

      {showCreateForm && (
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            Create New Tenant
          </h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Tenant Name *</label>
                <input
                  style={inputStyle}
                  value={createForm.tenant_name}
                  onChange={(e) => setCreateForm({ ...createForm, tenant_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Admin Email *</label>
                <input
                  style={inputStyle}
                  type="email"
                  value={createForm.admin_email}
                  onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Password (if new user)</label>
                <input
                  style={inputStyle}
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Trial Days</label>
                <input
                  style={inputStyle}
                  type="number"
                  value={createForm.trial_days}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, trial_days: parseInt(e.target.value) || 60 })
                  }
                />
              </div>
              <div>
                <label style={labelStyle}>Special Code</label>
                <input
                  style={inputStyle}
                  value={createForm.special_code}
                  onChange={(e) => setCreateForm({ ...createForm, special_code: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'end', gap: '8px', paddingBottom: '4px' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_design_partner}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, is_design_partner: e.target.checked })
                  }
                />
                <label style={{ fontSize: '13px', color: '#374151' }}>Design Partner</label>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <button
                type="submit"
                disabled={creating}
                style={{
                  padding: '8px 20px',
                  background: '#0E1333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: creating ? 0.7 : 1,
                }}
              >
                {creating ? 'Creating...' : 'Create Tenant'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              {['Name', 'Owner', 'Users', 'Billing', 'Trial Ends', 'Partner', 'Actions'].map(
                (header) => (
                  <th
                    key={header}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#64748b',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {header}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr
                key={tenant.id}
                style={{ borderBottom: '1px solid #f1f5f9' }}
              >
                <td style={cellStyle}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{tenant.name}</span>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {tenant.id}</div>
                </td>
                <td style={cellStyle}>{tenant.owner_email || '—'}</td>
                <td style={cellStyle}>{tenant.user_count}</td>
                <td style={cellStyle}>
                  {editingTenantId === tenant.id ? (
                    <select
                      value={editForm.billing_state || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, billing_state: e.target.value })
                      }
                      style={inputStyle}
                    >
                      {BILLING_STATE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    billingStateBadge(tenant.billing_state)
                  )}
                </td>
                <td style={cellStyle}>
                  {editingTenantId === tenant.id ? (
                    <input
                      type="date"
                      value={editForm.trial_ends_at || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, trial_ends_at: e.target.value })
                      }
                      style={inputStyle}
                    />
                  ) : (
                    formatDate(tenant.trial_ends_at)
                  )}
                </td>
                <td style={cellStyle}>
                  {editingTenantId === tenant.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.is_design_partner || false}
                      onChange={(e) =>
                        setEditForm({ ...editForm, is_design_partner: e.target.checked })
                      }
                    />
                  ) : tenant.is_design_partner ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: '#f0fdf4',
                        color: '#166534',
                      }}
                    >
                      Yes
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={cellStyle}>
                  {editingTenantId === tenant.id ? (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={handleSaveEdit} style={actionButtonStyle}>
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        style={{ ...actionButtonStyle, background: '#e2e8f0', color: '#475569' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEditing(tenant)}
                      style={actionButtonStyle}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {tenants.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>
                  No tenants found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#64748b',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
};

const cellStyle: React.CSSProperties = {
  padding: '12px 16px',
  color: '#475569',
};

const actionButtonStyle: React.CSSProperties = {
  padding: '4px 12px',
  background: '#0E1333',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 500,
};

export default PlatformAdminPage;
